import { getVideoSrc, isYoutube } from "~/utils/helper/video";

export default function (media, video) {
  const progress = ref(0);

  // YT exclusive variables
  const { $youtubeIframeApi } = useNuxtApp();
  const ytPlayer = ref(null);
  let youtubeProgressTimer = null;

  const trackStartVideo = ref(false);

  let progress25 = false;
  let progress50 = false;
  let progress75 = false;
  let progress100 = false;

  const startYouTubeProgressTracking = () => {
    stopYouTubeProgressTracking();

    youtubeProgressTimer = window.setInterval(() => {
      updateProgress();
    }, 1000);
  };

  const stopYouTubeProgressTracking = () => {
    if (youtubeProgressTimer) {
      clearInterval(youtubeProgressTimer);
      youtubeProgressTimer = null;
    }
  };

  const onYouTubePlayerStateChange = (event) => {
    const PlayerState = window.YT.PlayerState;

    if (event.data === PlayerState.PLAYING) {
      trackStartVideo.value = true;
      startYouTubeProgressTracking();
    }

    if (event.data === PlayerState.PAUSED) {
      trackPauseVideoEvent();
      stopYouTubeProgressTracking();
    }

    if (event.data === PlayerState.BUFFERING) {
      stopYouTubeProgressTracking();
    }

    if (event.data === PlayerState.ENDED) {
      stopYouTubeProgressTracking();
      trackCompletedVideoEvent();
    }
  };

  const getVideoDuration = () => {
    if (isYoutube(video)) {
      return ytPlayer?.value?.getDuration?.();
    }
    return media.value?.duration;
  };

  const getCurrentTime = () => {
    if (isYoutube(video)) {
      return ytPlayer.value?.getCurrentTime?.();
    }

    return media.value?.currentTime;
  };

  const getTrackedVideoUrl = () => {
    if (isYoutube(video)) {
      return ytPlayer.value?.getVideoUrl?.();
    }

    return video.url ?? getVideoSrc(video?.asset) ?? media.value?.src;
  };

  const updateProgress = () => {
    const duration = Number(getVideoDuration()) || 0;
    const currentTime = Number(getCurrentTime()) || 0;

    // clamp to [0, 1] so we never emit >100% progress
    const rawProgress = currentTime / duration;
    progress.value = Math.min(1, Math.max(0, rawProgress));

    if (!progress25 && progress.value >= 0.25) {
      progress25 = true;
      trackProgressVideoEvent("25");
    }

    if (!progress50 && progress.value >= 0.5) {
      progress50 = true;
      trackProgressVideoEvent("50");
    }

    if (!progress75 && progress.value >= 0.75) {
      progress75 = true;
      trackProgressVideoEvent("75");
    }
    // videos do not reach 1.00 state on loop, skipping this event trigger
    if (!progress100 && progress.value >= 0.99) {
      progress100 = true;
      trackProgressVideoEvent("100");
    }
  };

  const initYoutubePlayer = async (iframeEl) => {
    if (ytPlayer.value || !iframeEl) return false;

    try {
      if (!$youtubeIframeApi?.loadYouTubeIframeApi) {
        throw new Error("YouTube iframe API plugin is not available");
      }
      await $youtubeIframeApi.loadYouTubeIframeApi();
      ytPlayer.value = new window.YT.Player(iframeEl, {
        events: { onStateChange: onYouTubePlayerStateChange },
      });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  onMounted(async () => {
    if (!isYoutube(video) || !media.value?.getAttribute?.("src")?.trim()) return;

    await initYoutubePlayer(media.value);
  });

  onBeforeUnmount(() => {
    stopYouTubeProgressTracking();
    ytPlayer.value?.destroy?.();
  });

  watchOnce(trackStartVideo, () => {
    if (trackStartVideo.value === true) {
      trackPlayVideoEvent();
    }
  });

  const trackOnVideoProgress = () => {
    updateProgress();
  };

  const videoProvider = computed(() => {
    if (video?.asset) {
      return "Magnolia";
    }
    if (isYoutube(video)) {
      return "Youtube";
    }
    return "BOA";
  });

  const gtm = useGtm();
  const trackVideoEvent = (eventName, payload = {}) => {
    try {
      gtm?.trackEvent({
        event: `gx.${eventName}`,
        event_name: eventName,
        event_source: "source_code",
        [eventName]: {
          video_position: video["@path"],
          duration: getVideoDuration(),
          provider: videoProvider.value,
          url: getTrackedVideoUrl(),
          ...payload,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const trackPlayVideoEvent = () => {
    trackVideoEvent("video_play", { video_action: "play" });
  };

  const trackPauseVideoEvent = () => {
    trackVideoEvent("video_pause", { video_action: "pause" });
  };

  const trackCompletedVideoEvent = () => {
    trackVideoEvent("video_completed", { video_action: "video_end" });
  };

  const trackProgressVideoEvent = (percent) => {
    trackVideoEvent("video_progress", { percentage: percent });
  };

  return {
    trackPlayVideoEvent,
    trackCompletedVideoEvent,
    trackProgressVideoEvent,
    trackOnVideoProgress,
    trackStartVideo,
    trackPauseVideoEvent,
    updateProgress,
  };
}
